import type { ReactNode } from 'react';
import { DatePicker, Input, InputNumber, Select } from 'antd';
import type { FormInstance } from 'antd';
import moment from 'moment';
import type { Moment } from 'moment';

import type { PromoFieldConfig } from '../../../config/tpoGenConfig';
import type { ResolvedDateConstraints } from '../../../rules/types';
import UnitToggle from './UnitToggle';
import RangeInput from './RangeInput';

type Option = { label: string; value: string };

const renderPromoField = (
    field: PromoFieldConfig,
    options: Option[],
    disabled: boolean,
    formValues: Record<string, any>,
    form: FormInstance,
    planYear?: number,
    onValuesChange?: (changedValues: Record<string, unknown>) => void,
    dateConstraints?: ResolvedDateConstraints,
): ReactNode => {
    switch (field.type) {
        case 'datepicker': {
            const minDate = dateConstraints?.minDate as Moment | undefined;
            const maxDate = dateConstraints?.maxDate as Moment | undefined;
            const disablePast = dateConstraints?.disablePast;
            const planYearMinDate =
                dateConstraints?.minFromContext === 'planYearStart' && typeof planYear === 'number'
                    ? moment().year(planYear).startOf('year')
                    : undefined;
            const hasConstraints = !!(minDate || maxDate || disablePast || planYearMinDate);
            const disabledDate = hasConstraints
                ? (current: Moment) => {
                      if (!current) return false;
                      if (planYearMinDate && current.isBefore(planYearMinDate, 'day')) return true;
                      if (disablePast && current.isBefore(moment(), 'day')) return true;
                      if (minDate && current.isBefore(minDate, 'day')) return true;
                      if (maxDate && current.isAfter(maxDate, 'day')) return true;
                      return false;
                  }
                : undefined;
            const defaultPickerCandidates = [
                planYearMinDate,
                disablePast ? moment().startOf('day') : undefined,
                minDate,
            ].filter((value): value is Moment => !!value);
            const defaultPickerValue =
                defaultPickerCandidates.length > 0
                    ? defaultPickerCandidates.reduce((latestDate, candidateDate) =>
                          candidateDate.isAfter(latestDate, 'day') ? candidateDate : latestDate,
                      )
                    : undefined;
            return (
                <DatePicker
                    placeholder={field.placeholder}
                    allowClear={false}
                    disabled={disabled}
                    disabledDate={disabledDate}
                    defaultPickerValue={defaultPickerValue}
                    style={{ width: '100%', borderRadius: 6 }}
                />
            );
        }
        case 'select':
            return (
                <Select
                    placeholder={field.placeholder}
                    options={options}
                    disabled={disabled}
                    allowClear={field.allowClear ?? true}
                />
            );
        case 'offerlibrary':
            // TODO: integrate lazy-fetch of promo library mechanics on dropdown open
            return <Select placeholder={field.placeholder} options={options} disabled={disabled} allowClear />;
        case 'number': {
            const unitAddon = field.unitToggle ? (
                <UnitToggle
                    config={field.unitToggle}
                    value={formValues[field.unitToggle.unitField]}
                    onChange={(unitValue) => {
                        form.setFieldsValue({ [field.unitToggle!.unitField]: unitValue });
                        onValuesChange?.({ [field.unitToggle!.unitField]: unitValue });
                    }}
                    disabled={disabled}
                />
            ) : null;
            return (
                <InputNumber
                    placeholder={field.placeholder}
                    disabled={disabled}
                    style={{ width: '100%' }}
                    addonAfter={unitAddon}
                />
            );
        }
        case 'range': {
            const unitAddon = field.unitToggle ? (
                <UnitToggle
                    config={field.unitToggle}
                    value={formValues[field.unitToggle.unitField]}
                    onChange={(unitValue) => {
                        form.setFieldsValue({ [field.unitToggle!.unitField]: unitValue });
                        onValuesChange?.({ [field.unitToggle!.unitField]: unitValue });
                    }}
                    disabled={disabled}
                />
            ) : null;
            return (
                <RangeInput
                    labels={field.rangeLabels ?? { first: 'Min', second: 'Max' }}
                    placeholder={field.placeholder}
                    disabled={disabled}
                    unitToggle={unitAddon}
                />
            );
        }
        default:
            return <Input placeholder={field.placeholder} disabled={disabled} maxLength={field.maxCharLimit} />;
    }
};

export default renderPromoField;
